import type { Fish } from './fish';
import type { Wall } from './state';

export interface SvgWorld {
    width: number;
    height: number;
}

export interface ExportSvgOptions {
    walls?: readonly Wall[];
    background?: string;
}

const HALF_PI = Math.PI * 0.5;

function fmt(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return Number(n.toFixed(2)).toString();
}

export function fishToPathData(fish: Fish): string {
    const angle = fish.velocity.angle();
    const cx = fish.location.x;
    const cy = fish.location.y;
    const base = fish.base;
    const length = fish.length;

    const x1 = cx + Math.cos(angle) * base;
    const y1 = cy + Math.sin(angle) * base;
    const xT = cx - Math.cos(angle) * length;
    const yT = cy - Math.sin(angle) * length;
    const x2 = cx + Math.cos(angle + HALF_PI) * base;
    const y2 = cy + Math.sin(angle + HALF_PI) * base;
    const x3 = cx + Math.cos(angle - HALF_PI) * base;
    const y3 = cy + Math.sin(angle - HALF_PI) * base;

    return `M ${fmt(x1)} ${fmt(y1)} Q ${fmt(x2)} ${fmt(y2)} ${fmt(xT)} ${fmt(yT)} Q ${fmt(x3)} ${fmt(y3)} ${fmt(x1)} ${fmt(y1)} Z`;
}

export function exportSvg(
    creatures: readonly Fish[],
    world: SvgWorld,
    opts: ExportSvgOptions = {},
): string {
    const w = String(Math.max(0, Math.floor(world.width)));
    const h = String(Math.max(0, Math.floor(world.height)));
    const bg = opts.background ?? '#ffffff';
    const walls = opts.walls ?? [];

    const lines: string[] = [];
    lines.push('<?xml version="1.0" encoding="UTF-8"?>');
    lines.push(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">`,
    );
    lines.push(`<rect width="${w}" height="${h}" fill="${bg}"/>`);

    for (const fish of creatures) {
        const d = fishToPathData(fish);
        const color = fish.baseColor;
        lines.push(`<path d="${d}" fill="${color}" stroke="${color}" stroke-width="2"/>`);
    }

    for (const wall of walls) {
        lines.push(
            `<line x1="${fmt(wall[0])}" y1="${fmt(wall[1])}" x2="${fmt(wall[2])}" y2="${fmt(wall[3])}" stroke="#000000" stroke-width="2"/>`,
        );
    }

    lines.push('</svg>');
    return lines.join('\n');
}

export interface DownloadSvgOptions {
    document: Document;
    window: Window;
    filename?: string;
}

export function downloadSvg(svgText: string, opts: DownloadSvgOptions): void {
    const filename = opts.filename ?? 'bnff-scene.svg';
    const doc = opts.document;
    const win = opts.window;
    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = doc.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    doc.body.appendChild(a);
    a.click();
    doc.body.removeChild(a);
    win.setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 0);
}
